import React, { Component } from 'react';
import { Link } from 'react-router-dom';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import BandasEventos from '../components/HomeEventoComponent';
import ModalPurchase from '../components/ModalPurchase';
import { PanelHome, Fila, SubTitulo, Vacio, TiraRevisar, Boton, COLOR } from '../components/HomePanelComponent';

import { validateSession } from '../utils/functions';
import { get, patch, post } from '../utils/apiClient';
import { convertToMoney } from '../utils/formats';
import { navigateTo } from '../utils/navigation';
import { conVueltaAlHome } from '../utils/appMode';

// Home de trabajo (retail). No es un menú de accesos: el menú ya está en el sidebar y
// repetirlo acá no agrega nada. Lo que agrega es lo que el sidebar NO puede mostrar —
// qué hay pendiente hoy y poder resolverlo sin entrar a cada pantalla.
//
// Cuatro paneles, uno por área de trabajo de una persona: despacho, reposición, compras
// y cobranza. Una señal nueva se cuelga del panel que le corresponde; si cada una pidiera
// panel propio serían diez y no entrarían. Lo que casi siempre está en cero (ML, mails,
// transferencias) va en la tira de abajo, que en verde ocupa una línea.
//
// Todo viene de UNA llamada (`GET /home/retail`). Un bloque en `null` significa que ESA
// consulta falló, no que no haya nada: el backend arma cada bloque con su propio
// try/catch para que un panel roto no deje a nadie sin puerta de entrada.

// `purchase.date` y las fechas de la API llegan como hora de pared 'YYYY-MM-DD HH:mm:ss'.
// Parsearlas con `new Date(texto)` las lee como UTC y a la noche el "hace 1 día" se
// corre un día entero. Se compara por calendario local, a mano.
function diasDesde(fechaPared) {
    if (!fechaPared) return null;
    const soloFecha = String(fechaPared).slice(0, 10);
    const partes = soloFecha.split('-').map(Number);
    if (partes.length !== 3 || partes.some(isNaN)) return null;
    const entonces = new Date(partes[0], partes[1] - 1, partes[2]);
    const hoy = new Date();
    const hoySinHora = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    return Math.round((hoySinHora - entonces) / 86400000);
}

function haceCuanto(fechaPared) {
    const dias = diasDesde(fechaPared);
    if (dias === null) return '';
    if (dias <= 0) return 'hoy';
    if (dias === 1) return 'ayer';
    return `hace ${dias} días`;
}

// Los modos de despacho, en el orden en que se trabajan. Cada uno es una tarea
// distinta —el Flex lo llevamos nosotros, el de agencia hay que ir a despacharlo, el
// de retiro espera al cliente— así que van agrupados y no mezclados en una lista sola.
// El subtítulo dice QUÉ hacer, no cómo se llama el modo.
// El orden NO es por volumen ni por canal: es por lo que hay que hacer. Primero los dos
// que sólo hay que dejar preparados porque los pasa a buscar la cadetería (el Flex también
// —`self_service` en ML significa que el envío corre por cuenta nuestra, no que lo lleve
// alguien de la tienda—), después el de agencia, que es el ÚNICO donde alguien tiene que
// salir del local, y al final el que ya no depende de nosotros.
//
// Flex y domicilio comparten la acción a propósito: lo que los separa es de dónde viene la
// venta y qué cadetería la lleva, y eso la fila ya lo dice.
const MODOS = [
    // Primero lo que todavía no está armado. Una venta a despachar NACE 'En preparación' y
    // recién después pasa a 'Envío pendiente': hasta que alguien la saque de la estantería
    // y la empaquete, no hay nada que entregarle al cadete. Es el primer trabajo del día y
    // por eso encabeza el panel.
    { clave: 'preparar', titulo: 'PARA PREPARAR — TODAVÍA NO ESTÁ ARMADO', accion: 'Preparar paquete', paraPreparar: true },
    { clave: 'flex', titulo: 'ML FLEX — LA PASA A BUSCAR LA CADETERÍA', accion: 'Despachado' },
    { clave: 'domicilio', titulo: 'ENVÍO A DOMICILIO — LA PASA A BUSCAR LA CADETERÍA', accion: 'Despachado' },
    { clave: 'agencia', titulo: 'ML AGENCIA — HAY QUE LLEVARLA A LA AGENCIA', accion: 'Despachado', salirDelLocal: true },
    { clave: 'retiro', titulo: 'ESPERANDO AL CLIENTE', accion: 'Retirado' },
];

// El corte por grupo lo hace el backend: manda las más viejas DE CADA modo, no las más
// viejas en general. La pantalla muestra lo que le llega y el contador del subtítulo dice
// cuántos hay en total.
// Un envío que lleva 2 días o más sin salir nos espera a NOSOTROS. Un retiro que espera
// al cliente no es nuestra urgencia por más que lleve una semana, así que el rojo no es
// por antigüedad sola: lo decide quién tiene la pelota.
const DIAS_PARA_URGENTE = 2;

// `purchase.status` 'En preparación'. La venta nace acá y pasa a 'Envío pendiente' cuando
// está pronta para salir.
const EN_PREPARACION = 10;

class HomePage extends Component {

    constructor() {
        const rolCacheado = Number(localStorage.getItem('role') || 0);
        super();
        this.state = {
            checked: !!localStorage.getItem('token'),
            role: rolCacheado,
            // null = todavía no llegó. Las pantallas muestran "…" y nunca un cero inventado.
            home: null,
            error: null,
            trabajando: {},
            // La venta que está abierta en el modal de preparación. 0 = ninguna.
            prepararId: 0,
        };
        this._desmontado = false;
    }

    async componentDidMount() {
        // `validateSession(true)` a propósito: sin el `true`, un rol que no sea admin con
        // la caja cerrada rebota a /caja y nunca ve el home — justo la pantalla que le
        // está avisando que la caja está cerrada.
        const role = await validateSession(true);
        this.setState({ checked: true, role });
        this.cargar();
    }

    componentWillUnmount() {
        this._desmontado = true;
    }

    cargar = () => get('home/retail')
        .then((data) => {
            if (this._desmontado) return;
            this.setState({ home: data, error: null });
        })
        .catch((error) => {
            if (this._desmontado) return;
            this.setState({ error: error.message || 'No se pudo cargar' });
        });

    // Resolver una tarea desde el home: se marca la fila como "en curso" para que no se
    // pueda clickear dos veces, y al terminar se recarga TODO. Recargar entero y no
    // sacar la fila a mano es a propósito: marcar un envío cambia también el contador y
    // puede hacer entrar en la lista una fila que estaba fuera del tope de 8.
    resolver = (clave, promesa) => {
        this.setState({ trabajando: { ...this.state.trabajando, [clave]: true } });
        promesa
            .then(() => this.cargar())
            .catch((error) => alert(error.message || 'No se pudo completar la operación'))
            .then(() => {
                if (this._desmontado) return;
                const trabajando = { ...this.state.trabajando };
                delete trabajando[clave];
                this.setState({ trabajando });
            });
    }

    // Mismo criterio que el detalle de la venta: un envío pendiente pasa a Entregado (8)
    // y cualquier otro a Retirado (5).
    marcarEnvio = (item) => this.resolver(
        `envio-${item.shippingId}`,
        patch('shipping/status', {
            shippingId: Number(item.shippingId),
            status: Number(item.estado) === 4 ? 8 : 5,
        })
    );

    marcarRepuesto = (item) => this.resolver(
        `estante-${item.code}`,
        post('shelf-restock/done', { lines: item.lines, kind: 'restocked' })
    );

    onBuscarKeyDown = (event) => {
        // keyCode 13 además de 'Enter': las pistolas de código de barras mandan el retorno
        // con otros nombres de tecla.
        if (event.key !== 'Enter' && event.keyCode !== 13) return;
        const valor = String(event.target.value || '').trim();
        navigateTo(valor ? `/priceCheck?q=${encodeURIComponent(valor)}` : '/priceCheck');
    }

    // ── Paneles ──────────────────────────────────────────────────────────────

    panelDespacho() {
        const bloque = this.state.home ? this.state.home.despacho : null;
        if (!bloque) {
            return (
                <PanelHome icono="local_shipping" titulo="Despacho" verTodo="/shipping" cargando={!this.state.home}>
                    <Vacio texto="No se pudo cargar el despacho." />
                </PanelHome>
            );
        }

        // `items` se lee siempre a través de una variable con default: un bloque que llega
        // con una forma distinta a la esperada (un backend más viejo, un campo que se
        // renombró) tiene que dejar el panel pobre, no tumbar la pantalla de aterrizaje
        // entera. Es el mismo criterio que el try/catch por bloque del backend.
        const filas = bloque.items || [];
        const porModo = bloque.porModo || {};
        const total = bloque.porDespachar + bloque.esperandoRetiro;
        // El mismo criterio que el backend: la compra 'En preparación' manda sobre el modo
        // de envío, porque hasta que no esté armado el transporte no importa.
        const modoDe = (item) => {
            if (Number(item.estadoCompra) === EN_PREPARACION) return 'preparar';
            return item.transporte ? item.transporte.modo : 'domicilio';
        };

        return (
            <PanelHome
                icono="local_shipping" titulo="Despacho"
                contador={total} tono={bloque.porDespachar > 0 ? 'alerta' : 'gris'}
                verTodo="/shipping"
            >
                {total === 0 ? <Vacio texto="No queda nada por despachar ni por entregar." /> : null}

                {MODOS.map((modo) => {
                    const cuantos = Number(porModo[modo.clave] || 0);
                    if (cuantos === 0) {
                        return null;
                    }
                    const suyas = filas.filter((item) => modoDe(item) === modo.clave);
                    // Lo que hay que preparar se muestra siempre en rojo: es lo único del
                    // panel donde el paquete todavía no existe.
                    return (
                        <React.Fragment key={modo.clave}>
                            <SubTitulo texto={`${modo.titulo} · ${cuantos}`} tono={modo.salirDelLocal ? 'salida' : null} />
                            {suyas.map((item, indice) => this.filaEnvio(item, indice === 0, modo.accion, modo.paraPreparar))}
                            {suyas.length < cuantos ? (
                                <div style={{ padding: '5px 16px 8px 16px', fontSize: 12, color: COLOR.apagado }}>
                                    + {cuantos - suyas.length} más
                                </div>
                            ) : null}
                        </React.Fragment>
                    );
                })}
            </PanelHome>
        );
    }

    filaEnvio(item, primero, accion, paraPreparar) {
        const dias = diasDesde(item.fecha);
        // Sólo lo que nos espera a NOSOTROS se pinta urgente: un retiro que espera al
        // cliente no es nuestra demora por más que lleve una semana.
        const urgente = paraPreparar || (accion !== 'Retirado' && dias !== null && dias >= DIAS_PARA_URGENTE);
        // Qué empaquetar: es el dato por el que existe este panel y no un link a Envíos.
        const productos = (item.productos || [])
            .map((producto) => (producto.cantidad > 1 ? `${producto.cantidad}× ${producto.nombre}` : producto.nombre))
            .join(' · ');
        const trabajando = this.state.trabajando[`envio-${item.shippingId}`];
        // El destino Y la cadetería: sin la cadetería, un Flex y una venta de la página
        // con DAC se ven exactamente iguales, que es lo que hace ilegible la lista.
        const cadeteria = item.transporte ? item.transporte.cadeteria : null;

        return (
            <Fila
                key={item.shippingId}
                primero={primero}
                titulo={`#${item.ticketId || item.purchaseId} · ${productos || 'sin detalle'}`}
                detalle={[cadeteria, item.destino || item.direccion].filter(Boolean).join(' · ')}
                meta={haceCuanto(item.fecha)}
                urgente={urgente}
                accion={trabajando ? '…' : accion}
                destacada={paraPreparar}
                // Preparar abre el MISMO modal de la pantalla de Ventas, en su modo de
                // preparación (`status={10}`): lista los productos y se van bipeando uno
                // por uno hasta "Confirmar preparación". Se abre acá y no se navega, que
                // es de lo que se trata un home de trabajo.
                atributos={paraPreparar ? { 'data-toggle': 'modal', 'data-target': '#modal-view' } : undefined}
                onAccion={trabajando
                    ? undefined
                    : paraPreparar
                        ? () => this.setState({ prepararId: item.purchaseId })
                        : () => this.marcarEnvio(item)}
            />
        );
    }

    panelReposicion() {
        const estanteria = this.state.home ? this.state.home.estanteria : null;

        if (!estanteria) {
            return (
                <PanelHome icono="playlist_add_check" titulo="Reposición" verTodo="/shelfRestock" cargando={!this.state.home}>
                    <Vacio texto="No se pudo cargar la estantería." />
                </PanelHome>
            );
        }

        const juegos = estanteria.items || [];

        return (
            <PanelHome
                icono="playlist_add_check" titulo="Reposición"
                contador={estanteria.products} tono={estanteria.products > 0 ? 'alerta' : 'gris'}
                verTodo="/shelfRestock"
            >
                {estanteria.products === 0
                    ? <Vacio texto="La estantería está al día." />
                    : <SubTitulo texto="VENDIDOS POR MOSTRADOR, SIN REPONER" />}

                {juegos.map((item, indice) => {
                    const trabajando = this.state.trabajando[`estante-${item.code}`];
                    return (
                        <Fila
                            key={item.code}
                            primero={indice === 0}
                            titulo={item.nombre}
                            detalle={item.pendientes > 1 ? `${item.pendientes} unidades` : null}
                            meta={`quedan ${item.stock}`}
                            urgente={item.atrasado}
                            accion={trabajando ? '…' : 'Repuesto'}
                            onAccion={trabajando ? undefined : () => this.marcarRepuesto(item)}
                        />
                    );
                })}

                {estanteria.products > juegos.length ? (
                    <div style={{ padding: '7px 16px 9px 16px', fontSize: 12, color: COLOR.apagado }}>
                        + {estanteria.products - juegos.length} más
                        {estanteria.waitingStock && estanteria.waitingStock.products > 0
                            ? <span style={{ color: COLOR.aviso }}> · {estanteria.waitingStock.products} esperan stock</span>
                            : null}
                    </div>
                ) : null}
            </PanelHome>
        );
    }

    panelCompras() {
        const bloque = this.state.home ? this.state.home.compras : null;
        if (!bloque) {
            return (
                <PanelHome icono="shopping_basket" titulo="Compras" cargando={!this.state.home}>
                    <Vacio texto="No se pudo cargar compras." />
                </PanelHome>
            );
        }

        const hayAlgo = bloque.faltantes || bloque.pedidosAbiertos > 0 || bloque.facturasVencidas > 0;

        return (
            <PanelHome
                icono="shopping_basket" titulo="Compras"
                contador={bloque.pedidosAbiertos} tono="gris"
            >
                {!hayAlgo ? <Vacio texto="Nada bajo mínimo ni pedidos abiertos." /> : null}

                {bloque.faltantes ? (
                    <Fila
                        primero
                        titulo="Hay productos bajo el mínimo"
                        detalle="Listos para armar el pedido"
                        accion="Hacer pedido"
                        href="/stockMonitor"
                        urgente
                        destacada
                    />
                ) : null}

                {bloque.pedidosAbiertos > 0 ? (
                    <Fila
                        primero={!bloque.faltantes}
                        titulo={`${bloque.pedidosAbiertos} ${bloque.pedidosAbiertos === 1 ? 'pedido abierto' : 'pedidos abiertos'}`}
                        detalle="Esperando que llegue la mercadería"
                        accion="Ver"
                        href="/providerOrders"
                    />
                ) : null}

                {bloque.facturasVencidas > 0 ? (
                    <Fila
                        titulo={`${bloque.facturasVencidas} ${bloque.facturasVencidas === 1 ? 'factura vencida' : 'facturas vencidas'}`}
                        detalle="Plata que se debe, con fecha pasada"
                        accion="Pagar"
                        href="/providerInvoices"
                        urgente
                    />
                ) : null}
            </PanelHome>
        );
    }

    panelCobranza() {
        const bloque = this.state.home ? this.state.home.cobranza : null;

        const pie = bloque && bloque.usadosPorAcreditar > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ flexGrow: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLOR.texto }}>
                        {bloque.usadosPorAcreditar} {bloque.usadosPorAcreditar === 1 ? 'usado' : 'usados'} esperando acreditar
                    </div>
                    <div style={{ fontSize: 11, color: COLOR.apagado, marginTop: 1 }}>Crédito prometido al que trajo el juego</div>
                </div>
                <Boton texto="Acreditar" href="/usedGames" />
            </div>
        ) : null;

        if (!bloque) {
            return (
                <PanelHome icono="request_quote" titulo="Cobranza" cargando={!this.state.home}>
                    <Vacio texto="No se pudo cargar cobranza." />
                </PanelHome>
            );
        }

        const ventas = bloque.items || [];

        return (
            <PanelHome
                icono="request_quote" titulo="Cobranza"
                contador={bloque.esperandoPago} tono={bloque.esperandoPago > 0 ? 'aviso' : 'gris'}
                verTodo="/purchase" pie={pie}
            >
                {bloque.esperandoPago === 0
                    ? <Vacio texto="No hay ventas esperando pago." />
                    : <SubTitulo texto={`ESPERANDO PAGO · ${bloque.esperandoPago}`} />}

                {ventas.map((item, indice) => (
                    <Fila
                        key={item.purchaseId}
                        primero={indice === 0}
                        titulo={`#${item.ticketId || item.purchaseId}${item.importe != null ? ` · $${convertToMoney(item.importe)}` : ''}`}
                        detalle={Number(item.estado) === 7 ? 'Sin pagar y sin entregar' : 'Pago pendiente'}
                        meta={haceCuanto(item.fecha)}
                        urgente={diasDesde(item.fecha) >= DIAS_PARA_URGENTE}
                        accion="Ver"
                        href="/purchase"
                    />
                ))}

                {bloque.esperandoPago > ventas.length ? (
                    <div style={{ padding: '7px 16px 9px 16px', fontSize: 12, color: COLOR.apagado }}>
                        + {bloque.esperandoPago - ventas.length} más
                    </div>
                ) : null}
            </PanelHome>
        );
    }

    // El conteo de hoy tiene banda propia, ancha y arriba de los paneles. Estaba como pie
    // del panel de Reposición —son parientes: los dos son stock físico— y ahí se leía como
    // nota al pie de otra cosa. Es una obligación DIARIA con lista cerrada: si el día
    // termina sin contar, no se recupera.
    bandaConteo() {
        const conteo = this.state.home ? this.state.home.conteo : null;

        if (!conteo) {
            return (
                <div className="card card-small mb-4">
                    <div style={{ padding: '14px 16px', fontSize: 13, color: COLOR.apagado }}>
                        {this.state.home ? 'No se pudo cargar el conteo de hoy.' : 'Conteo de hoy: …'}
                    </div>
                </div>
            );
        }

        // Sin lista no hay barra que mostrar: no es 0 % contado, es que todavía no hay qué contar.
        if (!conteo.listaArmada) {
            return (
                <div className="card card-small mb-4" style={{ borderLeft: `4px solid ${COLOR.aviso}` }}>
                    <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                        <i className="material-icons" style={{ fontSize: 26, color: COLOR.aviso }}>fact_check</i>
                        <div style={{ flexGrow: 1 }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: COLOR.texto }}>La lista de conteo de hoy no se armó</div>
                            <div style={{ fontSize: 12, color: COLOR.apagado, marginTop: 2 }}>Hasta que exista no hay nada que contar</div>
                        </div>
                        <Boton texto="Ir al conteo" href={conVueltaAlHome('/stockCount')} destacado />
                    </div>
                </div>
            );
        }

        const listo = conteo.pending === 0;
        const porcentaje = conteo.total > 0 ? Math.round((conteo.contados / conteo.total) * 100) : 0;
        const color = listo ? COLOR.ok : COLOR.alerta;

        return (
            <div className="card card-small mb-4" style={{ borderLeft: `4px solid ${color}` }}>
                <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                    <i className="material-icons" style={{ fontSize: 26, color }}>fact_check</i>

                    <div style={{ flexShrink: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: COLOR.texto }}>
                            {listo ? 'Conteo de hoy terminado' : `Faltan ${conteo.pending} de ${conteo.total} por contar`}
                        </div>
                        <div style={{ fontSize: 12, color: COLOR.apagado, marginTop: 2 }}>
                            {listo ? `Se contaron los ${conteo.total} de la lista` : `${conteo.contados} contados`}
                        </div>
                    </div>

                    {/* La barra es el dato que hace que esto se mire: "faltan 7" no dice si es
                        el principio o el final del día. */}
                    <div style={{ flexGrow: 1, minWidth: 160, height: 8, background: '#eef1f5', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${porcentaje}%`, height: '100%', background: color }} />
                    </div>

                    {conteo.diferencias > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLOR.aviso, display: 'inline-block' }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: COLOR.texto }}>
                                {conteo.diferencias} {conteo.diferencias === 1 ? 'diferencia' : 'diferencias'} sin resolver
                            </span>
                            <Boton texto="Resolver" href="/stockCountAdmin" />
                        </div>
                    ) : null}

                    <Boton texto={listo ? 'Ver el conteo' : 'Seguir contando'} href={conVueltaAlHome('/stockCount')} destacado={!listo} />
                </div>
            </div>
        );
    }

    tiraRevisar() {
        const bloque = this.state.home ? this.state.home.revisar : null;
        if (!bloque) {
            return null;
        }
        return (
            <TiraRevisar items={[
                {
                    titulo: bloque.mlCaidas > 0 ? `${bloque.mlCaidas} órdenes de ML caídas` : 'Órdenes ML al día',
                    alerta: bloque.mlCaidas > 0, ruta: '/failedMlOrders', accion: 'Reprocesar',
                },
                {
                    titulo: bloque.mailsFallidos > 0 ? `${bloque.mailsFallidos} mails no salieron` : 'Todos los mails salieron',
                    alerta: bloque.mailsFallidos > 0, ruta: '/emailLog', accion: 'Ver',
                },
                {
                    titulo: bloque.transferenciasPendientes > 0
                        ? `${bloque.transferenciasPendientes} ${bloque.transferenciasPendientes === 1 ? 'transferencia' : 'transferencias'} por recibir`
                        : 'Sin transferencias por recibir',
                    alerta: bloque.transferenciasPendientes > 0, ruta: '/stockTransfers', accion: 'Recibir', tono: 'aviso',
                },
            ]} />
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }
        const nombre = localStorage.getItem('firstName') || '';
        const caja = this.state.home ? this.state.home.caja : null;

        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4" style={{ paddingBottom: '60px' }}>
                            <HeaderComponent title="Inicio" subtitle={nombre ? `Hola, ${nombre}` : 'Tu día'} />

                            {this.state.error ? (
                                <div className="alert alert-warning" style={{ fontSize: 13 }}>
                                    No se pudo cargar el home: {this.state.error}
                                </div>
                            ) : null}

                            {/* Mostrador: lo que se toca con un cliente enfrente. */}
                            <div className="card card-small mb-4">
                                <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                                    <div style={{ flexGrow: 1, minWidth: 260, display: 'flex', alignItems: 'center' }}>
                                        <i className="material-icons" style={{ fontSize: 24, color: COLOR.apagado, marginRight: 10 }}>search</i>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Consultá un precio: bipeá el código o escribí el nombre y Enter"
                                            onKeyDown={this.onBuscarKeyDown}
                                            autoFocus
                                        />
                                    </div>
                                    <Link to={conVueltaAlHome('/addPurchase')} className="btn btn-primary" style={{ flexShrink: 0 }}>Nueva venta</Link>
                                    <CajaChip caja={caja} />
                                </div>
                            </div>

                            {/* El evento de hoy va ARRIBA del conteo: mientras dura, la puerta
                                es el trabajo más urgente del local. Sin evento no se dibuja. */}
                            <BandasEventos eventos={this.state.home ? this.state.home.eventosHoy : null} onCambio={this.cargar} />

                            {this.bandaConteo()}

                            <div className="row">
                                <div className="col-12 col-xl-6 mb-4">{this.panelDespacho()}</div>
                                <div className="col-12 col-xl-6 mb-4">{this.panelReposicion()}</div>
                                <div className="col-12 col-xl-6 mb-4">{this.panelCompras()}</div>
                                <div className="col-12 col-xl-6 mb-4">{this.panelCobranza()}</div>
                            </div>

                            {this.tiraRevisar()}

                            {/* El modal vive una sola vez en la página: es de Bootstrap y
                                se identifica por el id `#modal-view`, así que repetirlo por
                                fila abriría el primero siempre. */}
                            <ModalPurchase
                                mostrarPreparacion
                                purchaseId={this.state.prepararId}
                                status={10}
                                onRefresh={this.cargar}
                                onClose={() => this.setState({ prepararId: 0 })}
                            />
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

// La caja va en la barra de arriba y no en un panel: con la caja cerrada no se puede
// cobrar, así que es una condición del mostrador y no una tarea de una lista.
export function CajaChip({ caja }) {
    if (!caja) {
        return <span style={{ fontSize: 12, color: COLOR.apagado, flexShrink: 0 }}>Caja: …</span>;
    }
    if (caja.abierta) {
        return (
            <Link to="/caja" style={{ display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0, textDecoration: 'none' }}>
                <i className="material-icons" style={{ fontSize: 18, color: COLOR.ok }}>check</i>
                <span style={{ fontSize: 13, color: COLOR.texto }}>Caja abierta</span>
            </Link>
        );
    }
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: COLOR.alerta, display: 'inline-block' }} />
            <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLOR.alerta }}>Caja cerrada</div>
                <div style={{ fontSize: 11, color: COLOR.apagado }}>No se puede cobrar</div>
            </div>
            <Link to={conVueltaAlHome('/caja')} className="btn btn-sm btn-outline-danger" style={{ fontSize: 12 }}>Abrir</Link>
        </div>
    );
}

export default HomePage;
